import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Layers, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, user } = useAuth();
  
  const [isLogin, setIsLogin] = useState(searchParams.get("mode") !== "register");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    company_name: "",
    role: searchParams.get("role") || "advertiser"
  });

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        await register(formData);
        toast.success("Account created successfully!");
      }
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "An error occurred";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-white">MediaTNT</span>
          </div>

          <h1 className="font-heading text-2xl font-bold text-white mb-2" data-testid="auth-title">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-muted-foreground mb-8">
            {isLogin 
              ? "Enter your credentials to access your dashboard" 
              : "Start advertising or monetizing your traffic today"}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="bg-night-paper border-night-subtle focus:border-primary"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name (Optional)</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    placeholder="Acme Inc."
                    value={formData.company_name}
                    onChange={handleChange}
                    className="bg-night-paper border-night-subtle focus:border-primary"
                    data-testid="company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, role: "advertiser" }))}
                      className={`p-4 rounded-lg border transition-all ${
                        formData.role === "advertiser"
                          ? "border-primary bg-primary/10 text-white"
                          : "border-night-subtle bg-night-paper text-muted-foreground hover:border-primary/50"
                      }`}
                      data-testid="role-advertiser-btn"
                    >
                      <div className="font-semibold mb-1">Advertiser</div>
                      <div className="text-xs opacity-70">Run ad campaigns</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, role: "publisher" }))}
                      className={`p-4 rounded-lg border transition-all ${
                        formData.role === "publisher"
                          ? "border-emerald bg-emerald/10 text-white"
                          : "border-night-subtle bg-night-paper text-muted-foreground hover:border-emerald/50"
                      }`}
                      data-testid="role-publisher-btn"
                    >
                      <div className="font-semibold mb-1">Publisher</div>
                      <div className="text-xs opacity-70">Monetize traffic</div>
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-night-paper border-night-subtle focus:border-primary"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="bg-night-paper border-night-subtle focus:border-primary pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-primary hover:bg-indigo-hover btn-glow"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                isLogin ? "Sign In" : "Create Account"
              )}
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-6">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Sign up" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>

      {/* Right Panel - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-night-paper border-l border-night-subtle p-12">
        <motion.div 
          className="max-w-lg"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-transparent border border-primary/30 p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-4">
              {formData.role === "publisher" 
                ? "Maximize Your Ad Revenue"
                : "Launch Campaigns That Convert"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {formData.role === "publisher"
                ? "Join 5,000+ publishers earning more with our AI-optimized ad placements."
                : "Access premium inventory and AI-powered targeting to drive real results."}
            </p>
            <div className="space-y-3">
              {(formData.role === "publisher" 
                ? ["Higher eCPM rates", "Real-time analytics", "Header bidding support"]
                : ["Self-serve platform", "AI budget optimization", "Fraud protection"]
              ).map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className={`w-1.5 h-1.5 rounded-full ${formData.role === "publisher" ? "bg-emerald" : "bg-primary"}`} />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
